import { memo, useMemo } from 'react'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectPositionLtpsSignature } from '../../selectors/security'
import { toLocaleStringIn } from '../../util/general'
import { getPositionSecurityKeys, buildLtpsByKey } from '../../util/positionLtpsSignature'
import { computeBasketStrategyMtm } from '../../util/orderRowStrategyMtm'
import type { GroupedPosition } from '../../reducer/positions.reducer'

export type OrderRowLivePriceProps = {
    securityKey: string
    priceMultiplier: number
    isBasketStrategyAux: boolean
    position?: GroupedPosition
}

const OrderRowLivePrice: React.FC<OrderRowLivePriceProps> = ({
    securityKey,
    priceMultiplier,
    isBasketStrategyAux,
    position,
}) => {
    const useBasketMtm = Boolean(isBasketStrategyAux && position && (position.children?.length ?? 0) >= 1)
    const positionSecurityKeys = useMemo(
        () => (useBasketMtm && position ? getPositionSecurityKeys(position) : [securityKey]),
        [useBasketMtm, position, securityKey]
    )
    const ltpsSignature = useSelector(selectPositionLtpsSignature(positionSecurityKeys))

    const display = useMemo(() => {
        const ltpsByKey = buildLtpsByKey(positionSecurityKeys, ltpsSignature)
        if (useBasketMtm && position) {
            return toLocaleStringIn(computeBasketStrategyMtm(position, ltpsByKey))
        }
        const ltp = ltpsByKey.get(securityKey) ?? 0
        return toLocaleStringIn(ltp / (priceMultiplier || 100))
    }, [useBasketMtm, position, positionSecurityKeys, ltpsSignature, securityKey, priceMultiplier])

    return <Typography fontSize="13px">{display}</Typography>
}

export default memo(
    OrderRowLivePrice,
    (prev, next) =>
        prev.securityKey === next.securityKey &&
        prev.priceMultiplier === next.priceMultiplier &&
        prev.isBasketStrategyAux === next.isBasketStrategyAux &&
        prev.position === next.position
)
